import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type InboxPollAnswer = Record<string, any>
type PublicId = string

export interface InboxCacheEntry {
  conversations: any[]
  poll_answers?: InboxPollAnswer[]
  latestPollAnswerTs?: string | null
  ts?: number
}

type CacheValue = InboxCacheEntry | any[]

interface InboxCacheState {
  conversationsCache: Record<string, CacheValue>

  // Public IDs are now strings.
  conversationIndex: Record<PublicId, any>

  setCache: (key: string, payload: CacheValue) => void
  getCache: (key: string) => CacheValue | undefined
  getCacheEntry: (key: string) => InboxCacheEntry | undefined

  indexConversations: (conversations: any[]) => void
  getConversationById: (
    conversationId: PublicId
  ) => any | undefined

  getConversationByOtherUserId: (
    otherUserId: PublicId
  ) => any | undefined

  bumpUnread: (
    conversationId: PublicId,
    by?: number,
    opts?: {
      setLastMessageBody?: string | null
      setLastMessageCreatedAt?: string | null
    }
  ) => void

  resetUnread: (conversationId: PublicId) => void

  removeCache: (key: string) => void
  clear: () => void
}

const normalizeEntry = (
  payload: CacheValue | undefined
): InboxCacheEntry | undefined => {
  if (!payload) return undefined

  if (Array.isArray(payload)) {
    return {
      conversations: payload,
      poll_answers: [],
      latestPollAnswerTs: null,
    }
  }

  return payload
}

const getConversationId = (
  conversation: any
): PublicId | null => {
  const id =
    conversation?.conversation_id ??
    conversation?.conversation?.id ??
    conversation?.id ??
    null

  if (id === null || id === undefined || id === '') {
    return null
  }

  return String(id)
}

export const useInboxCacheStore =
  create<InboxCacheState>()(
    persist(
      (set, get) => ({
        conversationsCache: {},
        conversationIndex: {},

        indexConversations: (conversations) => {
          if (
            !Array.isArray(conversations) ||
            conversations.length === 0
          ) {
            return
          }

          set((state) => {
            const next = {
              ...state.conversationIndex,
            }

            for (const conversation of conversations) {
              const id =
                getConversationId(conversation)

              if (id) {
                next[id] = conversation
              }
            }

            return {
              conversationIndex: next,
            }
          })
        },

        setCache: (key, payload) => {
          set((state) => ({
            conversationsCache: {
              ...state.conversationsCache,
              [key]: payload,
            },
          }))

          const normalized =
            normalizeEntry(payload)

          if (
            normalized?.conversations?.length
          ) {
            get().indexConversations(
              normalized.conversations
            )
          }
        },

        getCache: (key) =>
          get().conversationsCache[key],

        getCacheEntry: (key) => {
          const raw =
            get().conversationsCache[key]

          return normalizeEntry(raw)
        },

        getConversationById: (
          conversationId
        ) => {
          return get().conversationIndex[
            String(conversationId)
          ]
        },

        getConversationByOtherUserId: (
          otherUserId
        ) => {
          const targetId =
            String(otherUserId)

          const index =
            get().conversationIndex

          for (const conversation of Object.values(
            index
          )) {
            const foundId =
              conversation?.other_users?.[0]?.id ??
              conversation?.other_user_id

            if (
              foundId !== null &&
              foundId !== undefined &&
              String(foundId) === targetId
            ) {
              return conversation
            }
          }

          return undefined
        },

        bumpUnread: (
          conversationId,
          by = 1,
          opts
        ) => {
          const normalizedConversationId =
            String(conversationId)

          const delta = Number(by ?? 1)

          if (
            !normalizedConversationId ||
            !Number.isFinite(delta) ||
            delta === 0
          ) {
            return
          }

          set((state) => {
            const nextIndex = {
              ...state.conversationIndex,
            }

            const existing =
              nextIndex[
                normalizedConversationId
              ]

            if (existing) {
              const currentUnread = Number(
                existing?.unread_messages ?? 0
              )

              nextIndex[
                normalizedConversationId
              ] = {
                ...existing,
                unread_messages: Math.max(
                  0,
                  currentUnread + delta
                ),
              }

              if (opts?.setLastMessageBody != null) {
                nextIndex[normalizedConversationId] = {
                  ...nextIndex[normalizedConversationId],
                  last_message: opts.setLastMessageBody,
                }
              }

              if (opts?.setLastMessageCreatedAt != null) {
                nextIndex[normalizedConversationId] = {
                  ...nextIndex[normalizedConversationId],
                  last_activity_at: opts.setLastMessageCreatedAt,
                }
              }
            }

            const nextCache: Record<
              string,
              CacheValue
            > = {
              ...state.conversationsCache,
            }

            for (const [key, raw] of Object.entries(
              state.conversationsCache
            )) {
              const entry =
                normalizeEntry(raw)

              if (
                !entry?.conversations?.length
              ) {
                continue
              }

              let touched = false

              const nextConversations =
                entry.conversations.map(
                  (conversation: any) => {
                    const id =
                      getConversationId(
                        conversation
                      )

                    if (
                      id !==
                      normalizedConversationId
                    ) {
                      return conversation
                    }

                    touched = true

                    const currentUnread =
                      Number(
                        conversation
                          ?.unread_messages ?? 0
                      )

                    let nextConversation = {
                      ...conversation,

                      unread_messages:
                        Math.max(
                          0,
                          currentUnread +
                            delta
                        ),
                    }

                    if (opts?.setLastMessageBody != null) {
                      nextConversation = {
                        ...nextConversation,
                        last_message: opts.setLastMessageBody,
                      }
                    }

                    if (opts?.setLastMessageCreatedAt != null) {
                      nextConversation = {
                        ...nextConversation,
                        last_activity_at: opts.setLastMessageCreatedAt,
                      }
                    }

                    return nextConversation
                  }
                )

              if (!touched) continue

              const nextEntry: InboxCacheEntry =
                {
                  ...entry,
                  conversations:
                    nextConversations,
                  ts: Date.now(),
                }

              nextCache[key] = Array.isArray(
                raw
              )
                ? nextEntry.conversations
                : nextEntry
            }

            return {
              conversationIndex: nextIndex,
              conversationsCache: nextCache,
            }
          })
        },

        resetUnread: (conversationId) => {
          const normalizedConversationId =
            String(conversationId)

          if (!normalizedConversationId) {
            return
          }

          set((state) => {
            const nextIndex = {
              ...state.conversationIndex,
            }

            const existing =
              nextIndex[
                normalizedConversationId
              ]

            if (
              existing &&
              Number(
                existing?.unread_messages ?? 0
              ) !== 0
            ) {
              nextIndex[
                normalizedConversationId
              ] = {
                ...existing,
                unread_messages: 0,
              }
            }

            const nextCache: Record<
              string,
              CacheValue
            > = {
              ...state.conversationsCache,
            }

            for (const [key, raw] of Object.entries(
              state.conversationsCache
            )) {
              const entry =
                normalizeEntry(raw)

              if (
                !entry?.conversations?.length
              ) {
                continue
              }

              let touched = false

              const nextConversations =
                entry.conversations.map(
                  (conversation: any) => {
                    const id =
                      getConversationId(
                        conversation
                      )

                    if (
                      id !==
                      normalizedConversationId
                    ) {
                      return conversation
                    }

                    if (
                      Number(
                        conversation
                          ?.unread_messages ?? 0
                      ) === 0
                    ) {
                      return conversation
                    }

                    touched = true

                    return {
                      ...conversation,
                      unread_messages: 0,
                    }
                  }
                )

              if (!touched) continue

              const nextEntry: InboxCacheEntry =
                {
                  ...entry,
                  conversations:
                    nextConversations,
                  ts: Date.now(),
                }

              nextCache[key] = Array.isArray(
                raw
              )
                ? nextEntry.conversations
                : nextEntry
            }

            return {
              conversationIndex: nextIndex,
              conversationsCache: nextCache,
            }
          })
        },

        removeCache: (key) => {
          const {
            [key]: _removed,
            ...updated
          } = get().conversationsCache

          set({
            conversationsCache: updated,
          })
        },

        clear: () =>
          set({
            conversationsCache: {},
            conversationIndex: {},
          }),
      }),
      {
        name: 'inbox-cache',

        partialize: (state) => ({
          conversationsCache:
            state.conversationsCache,

          conversationIndex:
            state.conversationIndex,
        }),
      }
    )
  )